/**
 * Speaker Label Normalization Utilities (Frontend)
 *
 * Mirrors api/lib/speaker-label-utils.ts for frontend transcript reconstruction.
 * Converts raw AssemblyAI speaker labels (e.g., "Speaker 3", "Instructor - 1")
 * to role-based display names (e.g., "Captain", "Instructor").
 */

// Role → display name mapping
const ROLE_DISPLAY_NAMES: Record<string, string | null> = {
  instructor: 'Instructor',
  captain: 'Captain',
  first_officer: 'First Officer',
  trainee: 'Trainee',
  student_pilot: 'Trainee',
  student: 'Trainee',
  other: null, // keep raw label
  unknown: null, // keep raw label
};

/**
 * Patterns to detect role hints in AssemblyAI speaker labels.
 * Each entry: [regex to match the label, base display name]
 */
const LABEL_HINT_PATTERNS: Array<[RegExp, string]> = [
  [/^instructor\s*[-]?\s*(\d+)?$/i, 'Instructor'],
  [/^student\s*pilot\s*[-]?\s*(\d+)?$/i, 'Student Pilot'],
  // "Trainee Captain" must come BEFORE "Captain" to avoid false match
  [/^trainee\s*captain\s*[-]?\s*(\d+)?$/i, 'First Officer'],
  [/^captain\s*[-]?\s*(\d+)?$/i, 'Captain'],
  [/^first\s*officer\s*[-]?\s*(\d+)?$/i, 'First Officer'],
  [/^trainee\s*[-]?\s*(\d+)?$/i, 'Trainee'],
];

export interface SpeakerRoleMapping {
  speaker_label: string;
  speaker_role: string;
}

/**
 * Build a lookup from raw speaker_label → display name.
 *
 * Two-phase approach:
 * 1. Use explicit speaker_role_mappings (e.g., "Speaker 3" → captain → "Captain")
 * 2. Infer from label hints (e.g., "INSTRUCTOR - 2" → "Instructor 2") for
 *    labels that have no explicit mapping.
 */
export function buildDisplayNameMap(
  speakerRoleMappings: SpeakerRoleMapping[],
  allSpeakerLabels?: string[]
): Record<string, string> {
  if (!speakerRoleMappings || speakerRoleMappings.length === 0) {
    if (allSpeakerLabels && allSpeakerLabels.length > 0) {
      return buildHintBasedDisplayNameMap(allSpeakerLabels);
    }
    return {};
  }

  const displayNameMap: Record<string, string> = {};
  const mappedLabels = new Set<string>();

  // Phase 1: Use explicit role mappings
  const roleGroups: Record<string, string[]> = {};
  for (const mapping of speakerRoleMappings) {
    const role = mapping.speaker_role;
    if (!roleGroups[role]) {
      roleGroups[role] = [];
    }
    roleGroups[role].push(mapping.speaker_label);
    mappedLabels.add(mapping.speaker_label.toLowerCase());
  }

  for (const [role, labels] of Object.entries(roleGroups)) {
    const baseName = ROLE_DISPLAY_NAMES[role];

    if (!baseName) {
      for (const label of labels) {
        displayNameMap[label] = label;
      }
      continue;
    }

    // All labels with the same role get the same display name (no numbering).
    // Multiple labels are diarization clusters of the same person, not different people.
    for (const label of labels) {
      displayNameMap[label] = baseName;
    }
  }

  // Phase 2: Infer from label hints for unmapped labels
  if (allSpeakerLabels) {
    const hintMap = buildHintBasedDisplayNameMap(
      allSpeakerLabels.filter(l => !mappedLabels.has(l.toLowerCase()))
    );
    for (const [label, displayName] of Object.entries(hintMap)) {
      if (!displayNameMap[label]) {
        displayNameMap[label] = displayName;
      }
    }
  }

  return displayNameMap;
}

/**
 * Build display names from AssemblyAI hint labels that have no explicit mapping.
 */
function buildHintBasedDisplayNameMap(labels: string[]): Record<string, string> {
  const displayNameMap: Record<string, string> = {};
  const hintGroups: Record<string, { baseName: string; labels: string[] }> = {};

  for (const label of labels) {
    for (const [pattern, baseName] of LABEL_HINT_PATTERNS) {
      if (pattern.test(label)) {
        if (!hintGroups[baseName]) {
          hintGroups[baseName] = { baseName, labels: [] };
        }
        hintGroups[baseName].labels.push(label);
        break;
      }
    }
  }

  // All labels matching the same hint get the same display name (no numbering).
  // Multiple labels are diarization clusters of the same person.
  for (const { baseName, labels: groupLabels } of Object.values(hintGroups)) {
    for (const label of groupLabels) {
      displayNameMap[label] = baseName;
    }
  }

  return displayNameMap;
}
